import React, { useState, useRef, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, Dimensions, TouchableOpacity, Modal, SafeAreaView, ActionSheetIOS, Platform, Alert, Animated, Linking } from 'react-native';
import { Image } from 'expo-image';
import { Video, ResizeMode } from 'expo-av';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Swipeable } from 'react-native-gesture-handler';
import { useTranslation } from 'react-i18next';
import Hyperlink from './Hyperlink';
import { Message } from '../types';
import { like, repost, unlike, unrepost, deletePost } from '../services/bluesky';
import { savePost } from '../utils/saved';
import { Theme, useThemedStyles, useTheme, useFontSize } from '../theme';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const MENU_CLOSE_DELAY_MS = 100; // Delay to ensure menu animation completes before showing alert

// Force line breaks in long continuous text for web
const processTextForWeb = (text: string): string => {
  if (Platform.OS !== 'web') return text;
  
  const MAX_CONTINUOUS_CHARS = 30;
  const lines = text.split('\n');
  
  return lines.map(line => {
    // Split by spaces
    const words = line.split(' ');
    return words.map(word => {
      // If word is too long, insert zero-width space every N characters
      if (word.length > MAX_CONTINUOUS_CHARS) {
        return word.match(new RegExp(`.{1,${MAX_CONTINUOUS_CHARS}}`, 'g'))?.join('\u200B') || word;
      }
      return word;
    }).join(' ');
  }).join('\n');
};

interface Props {
  message: Message;
  onReply?: (message: Message) => void;
  onQuote?: (message: Message) => void;
  onActionComplete?: () => void;
  onSelectUser?: (did: string) => void;
  onSelectPost?: (post: any) => void;
  onHashtagPress?: (hashtag: string) => void;
  onSearchHashtag?: (hashtag: string) => void;
  onHandlePress?: (handle: string) => void;
  fontSizeScale?: { small: number; default: number; large: number; xlarge: number };
}

const MessageBubble: React.FC<Props> = ({ message, onReply, onQuote, onActionComplete, onSelectUser, onSelectPost, onHashtagPress, onSearchHashtag, onHandlePress, fontSizeScale }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [showSensitive, setShowSensitive] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: 'image' | 'video' } | null>(null);
  const [windowWidth, setWindowWidth] = useState(Platform.OS === 'web' ? (typeof window !== 'undefined' ? window.innerWidth : SCREEN_WIDTH) : SCREEN_WIDTH);
  const swipeableRef = useRef<Swipeable>(null);
  const { theme } = useTheme();
  const { fontSizeScale: defaultFontSizeScale } = useFontSize();
  const activeFontSizeScale = fontSizeScale || defaultFontSizeScale;
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  // Track window resize on web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const isLiked = !!message.viewer?.like;
  const isReposted = !!message.viewer?.repost;

  const hashtags = useMemo(() => {
    if (!message.text) return [];
    const matches = message.text.match(/#[^\s#]+/g);
    return matches ? Array.from(new Set(matches)) : [];
  }, [message.text]);

  const handleLongPress = () => {
    console.log('📋 [MessageBubble] Menu opened for message:', message.uri);
    console.log('📋 [MessageBubble] isMine:', message.isMine);
    setMenuVisible(true);
  };

  const executeAction = async (index: number, data?: any) => {
    console.log('⚡ [MessageBubble] executeAction called with index:', index);
    // Close menu first for all cases
    setMenuVisible(false);
    
    try {
      switch (index) {
        case 0: // Like
          if (isLiked) {
            await unlike(message.viewer!.like!);
          } else {
            await like(message.uri, message.cid);
          }
          break;
        case 1: // Repost
          if (isReposted) {
            await unrepost(message.viewer!.repost!);
          } else {
            await repost(message.uri, message.cid);
          }
          break;
        case 2: // Quote
          if (onQuote) onQuote(message);
          return; // Don't refresh yet
        case 3: // Reply
          if (onReply) onReply(message);
          return; // Don't refresh yet
        case 4: // Delete
          console.log('🗑️ [MessageBubble] Delete case reached');
          // Delay alert slightly to ensure menu animation completes
          await new Promise(resolve => setTimeout(resolve, MENU_CLOSE_DELAY_MS));
          console.log('🗑️ [MessageBubble] Showing delete alert');
          
          const handleDelete = async () => {
            console.log('🗑️ [MessageBubble] Delete confirmed by user');
            try {
              console.log('🗑️ [MessageBubble] Starting delete for URI:', message.uri);
              const result = await deletePost(message.uri);
              console.log('✅ [MessageBubble] Delete successful:', result);
              if (onActionComplete) {
                console.log('🗑️ [MessageBubble] Calling onActionComplete');
                onActionComplete();
              }
            } catch (e: any) {
              console.error('❌ [MessageBubble] Delete failed:', e);
              Alert.alert(t('common.error'), e.message || t('messageBubble.deleteFailed'));
            }
          };
          
          if (Platform.OS === 'web') {
            // Web版ではwindow.confirmを使用
            console.log('🗑️ [MessageBubble] Using window.confirm for web');
            const confirmed = window.confirm(t('messageBubble.deleteDesc'));
            console.log('🗑️ [MessageBubble] Confirm result:', confirmed);
            if (confirmed) {
              await handleDelete();
            } else {
              console.log('🗑️ [MessageBubble] Delete cancelled by user');
            }
          } else {
            // ネイティブ版ではAlert.alertを使用
            Alert.alert(
              t('common.delete'),
              t('messageBubble.deleteDesc'),
              [
                { 
                  text: t('common.cancel'), 
                  style: 'cancel',
                  onPress: () => console.log('🗑️ [MessageBubble] Delete cancelled')
                },
                { 
                  text: t('common.delete'), 
                  style: 'destructive',
                  onPress: handleDelete
                }
              ]
            );
          }
          return;
        case 5: // Hashtag
          if (onHashtagPress && data) {
            const tag = typeof data === 'string' ? (data.endsWith(' ') ? data : data + ' ') : data;
            onHashtagPress(tag as string);
          }
          return;
        case 6: // Save
          await savePost({
            ...message,
            author: {
              avatar: message.avatarUrl,
              displayName: message.senderName,
              handle: message.authorDid,
            }
          });
          Alert.alert(t('messageBubble.saved'));
          break;
        default:
          return;
      }
      if (onActionComplete) onActionComplete();
    } catch (error: any) {
      Alert.alert(t('common.error'), error.message || t('messageBubble.actionFailed'));
    }
  };

  const openMedia = (url: string, type: 'image' | 'video') => {
    setSelectedMedia({ url, type });
    setModalVisible(true);
  };

  const renderRightActions = (progress: Animated.AnimatedInterpolation<number>, dragX: Animated.AnimatedInterpolation<number>) => {
    const scale = dragX.interpolate({
      inputRange: [-80, 0],
      outputRange: [1, 0],
      extrapolate: 'clamp',
    });

    return (
      <View style={styles.swipeActionContainer}>
        <Animated.View style={[styles.swipeActionIcon, { transform: [{ scale }] }]}>
          <Ionicons name="arrow-undo" size={24} color="#FFFFFF" />
        </Animated.View>
      </View>
    );
  };

  const handleSwipeOpen = () => {
    if (onReply) onReply(message);
    setTimeout(() => {
      swipeableRef.current?.close();
    }, 100);
  };

  const renderMedia = () => {
    if (!message.images && !message.video) {
      return null;
    }

    const hasLabel = message.labels && message.labels.length > 0;
    const labelText = message.labels?.map(l => l.val).join(', ');

    if (hasLabel && !showSensitive) {
      return (
        <TouchableOpacity 
          style={styles.sensitiveContainer} 
          onPress={() => setShowSensitive(true)}
        >
          <Ionicons name="eye-off" size={24} color="#FFFFFF" />
          <Text style={styles.sensitiveText}>閲覧注意: {labelText}</Text>
          <Text style={styles.sensitiveSubtext}>タップして表示</Text>
        </TouchableOpacity>
      );
    }
    
    return (
      <View style={styles.mediaContainer}>
        {message.images && message.images.map((img, index) => {
          const imageUrl = typeof img === 'string' ? img : img.url;
          const aspectRatio = typeof img === 'object' ? img.aspectRatio : undefined;
          
          let imageStyle: any = {
            width: '100%',
            maxWidth: '100%',
            borderRadius: 12,
            marginBottom: 4,
            backgroundColor: '#eee',
          };

          if (aspectRatio && aspectRatio.width && aspectRatio.height) {
            imageStyle.aspectRatio = aspectRatio.width / aspectRatio.height;
          } else {
            imageStyle.height = 200;
            imageStyle.width = '100%';
          }

          return (
            <TouchableOpacity key={index} onPress={() => openMedia(imageUrl, 'image')} onLongPress={handleLongPress}>
              <Image 
                source={{ uri: imageUrl }} 
                style={imageStyle} 
                contentFit="contain"
                cachePolicy="memory-disk"
              />
            </TouchableOpacity>
          );
        })}
        {message.video && (
          <TouchableOpacity onPress={() => openMedia(message.video!, 'video')} onLongPress={handleLongPress}>
            <View style={styles.videoThumbnailContainer}>
              <Video
                source={{ uri: message.video }}
                style={styles.videoThumbnail}
                resizeMode={ResizeMode.COVER}
                shouldPlay={false}
              />
              <View style={styles.playIconOverlay}>
                <Ionicons name="play" size={40} color="white" />
              </View>
            </View>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderReplyParent = () => {
    if (!message.replyParent) return null;
    return (
      <TouchableOpacity 
        style={styles.replyInsideContainer}
        onPress={() => onSelectPost?.(message.replyParent)}
        activeOpacity={0.7}
      >
        <View style={styles.replyInsideHeader}>
          {message.replyParent.avatarUrl && (
            <Image 
              source={{ uri: message.replyParent.avatarUrl }} 
              style={styles.replyInsideAvatar} 
              cachePolicy="memory-disk"
            />
          )}
          <View style={styles.replyInsideInfo}>
            <Text style={[styles.replyInsideAuthor, message.isMine && { color: theme.colors.myMessageText, opacity: 0.8 }]} numberOfLines={1}>
              {message.replyParent.senderName}
            </Text>
          </View>
        </View>
        <Hyperlink 
          linkDefault={true} 
          linkStyle={{ 
            color: message.isMine ? theme.colors.myMessageText : theme.colors.primary, 
            textDecorationLine: 'underline' 
          }} 
          style={[
            styles.replyInsideText,
            message.isMine && { color: theme.colors.myMessageText, opacity: 0.8 },
            Platform.OS === 'web' && {
              maxWidth: Math.floor(windowWidth * 0.7),
              width: '100%',
              display: 'inline-block',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }
          ]}
          onHandlePress={onHandlePress}
          onHashtagPress={onSearchHashtag}
        >
          {message.replyParent.text}
        </Hyperlink>
        <View style={styles.replySeparator} />
      </TouchableOpacity>
    );
  };

  const renderQuotedPost = () => {
    if (!message.quotedPost) return null;
    return (
      <TouchableOpacity 
        style={[
          styles.quoteContainer,
          message.isMine && { backgroundColor: theme.colors.messageBubble, opacity: 0.8 }
        ]}
        onPress={() => onSelectPost?.(message.quotedPost)}
        activeOpacity={0.7}
      >
        <View style={styles.quoteHeader}>
          {message.quotedPost.avatarUrl && (
            <Image 
              source={{ uri: message.quotedPost.avatarUrl }} 
              style={styles.quoteAvatar} 
              cachePolicy="memory-disk"
            />
          )}
          <Text style={[styles.quoteAuthor, message.isMine && { color: theme.colors.myMessageText }]} numberOfLines={1}>{message.quotedPost.senderName}</Text>
        </View>
        {message.quotedPost.text && (
          <Hyperlink 
            linkDefault={true} 
            linkStyle={{ 
              color: message.isMine ? theme.colors.myMessageText : theme.colors.primary, 
              textDecorationLine: 'underline' 
            }} 
            style={[
              styles.quoteText,
              message.isMine && { color: theme.colors.myMessageText },
              Platform.OS === 'web' && {
                maxWidth: Math.floor(windowWidth * 0.7),
                width: '100%',
                display: 'inline-block',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }
            ]}
            onHandlePress={onHandlePress}
            onHashtagPress={onSearchHashtag}
          >
            {message.quotedPost.text}
          </Hyperlink>
        )}
        {(message.quotedPost as any).images && (message.quotedPost as any).images.length > 0 && (
          <View style={{ marginTop: 8 }}>
            {(message.quotedPost as any).images.map((img: any, index: number) => {
              const imageUrl = typeof img === 'string' ? img : img.url;
              const aspectRatio = typeof img === 'object' ? img.aspectRatio : undefined;
              
              let imageStyle: any = {
                width: '100%',
                maxWidth: '100%',
                borderRadius: 8,
                marginBottom: 4,
                backgroundColor: '#eee',
              };

              if (aspectRatio && aspectRatio.width && aspectRatio.height) {
                imageStyle.aspectRatio = aspectRatio.width / aspectRatio.height;
              } else {
                imageStyle.height = 150;
                imageStyle.width = '100%';
              }

              return (
                <TouchableOpacity key={index} onPress={() => openMedia(imageUrl, 'image')}>
                  <Image 
                    source={{ uri: imageUrl }} 
                    style={imageStyle} 
                    contentFit="contain"
                    cachePolicy="memory-disk"
                  />
                </TouchableOpacity>
              );
            })}
          </View>
        )}
        {(message.quotedPost as any).video && (
          <View style={{ marginTop: 8 }}>
            <TouchableOpacity onPress={() => openMedia((message.quotedPost as any).video, 'video')}>
              <View style={[styles.videoThumbnailContainer, { height: 150 }]}>
                <Video
                  source={{ uri: (message.quotedPost as any).video }}
                  style={[styles.videoThumbnail, { height: 150 }]}
                  resizeMode={ResizeMode.COVER}
                  shouldPlay={false}
                />
                <View style={styles.playIconOverlay}>
                  <Ionicons name="play-circle" size={48} color="white" />
                </View>
              </View>
            </TouchableOpacity>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const renderRepostLabel = () => {
    if (!message.repostedBy) return null;
    return (
      <View style={[styles.repostLabelContainer, message.isMine ? styles.myRepostLabel : styles.otherRepostLabel]}>
        <MaterialCommunityIcons name="repeat" size={14} color={theme.colors.textMuted} />
        <Text style={styles.repostLabelText}>{t('messageBubble.repostedBy', { user: message.repostedBy })}</Text>
      </View>
    );
  };

  const renderExternal = () => {
    if (!message.external) return null;

    const { uri, title, description, thumb } = message.external;

    return (
      <TouchableOpacity 
        style={[
          styles.externalContainer,
          Platform.OS === 'web' && { maxWidth: Math.floor(windowWidth * 0.65) }
        ]} 
        onPress={() => Linking.openURL(uri)}
        activeOpacity={0.8}
      >
        {thumb && (
          <Image 
            source={{ uri: thumb }} 
            style={styles.externalThumb} 
            contentFit="cover"
          />
        )}
        <View style={styles.externalContent}>
          <Text style={styles.externalTitle} numberOfLines={2}>{title || uri}</Text>
          {description ? (
            <Text style={styles.externalDescription} numberOfLines={2}>{description}</Text>
          ) : null}
          <Text style={styles.externalUri} numberOfLines={1}>
            {uri.split('/')[2] || uri}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  if (message.isMine) {
    return (
      <Swipeable
        ref={swipeableRef}
        renderRightActions={renderRightActions}
        onSwipeableOpen={handleSwipeOpen}
        friction={2}
        rightThreshold={40}
        enabled={true}
      >
        <View style={styles.myMessageOuterContainer}>
          {renderRepostLabel()}
          <View style={styles.myMessageContainer}>
            <View style={styles.myContent}>
              {message.text || message.quotedPost || message.replyParent ? (
                <View style={styles.myBubbleRow}>
                  <View style={styles.myMessageInfo}>
                    {message.isRead && <Text style={styles.readText}>{t('chat.read')}</Text>}
                    <Text style={styles.timeText}>{message.createdAt}</Text>
                  </View>
                  <TouchableOpacity 
                    onPress={() => onSelectPost?.(message)}
                    onLongPress={handleLongPress} 
                    activeOpacity={0.8}
                  >
                    <View style={styles.myBubble}>
                      {renderReplyParent()}
                      {message.text ? (
                        <Hyperlink 
                          linkDefault={true} 
                          linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                          style={[
                            styles.messageText,
                            styles.myMessageText,
                            { fontSize: activeFontSizeScale.default },
                            Platform.OS === 'web' && {
                              maxWidth: Math.floor(windowWidth * 0.65),
                              width: '100%',
                              display: 'inline-block',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }
                          ]}
                          onHashtagPress={onSearchHashtag}
                          onHandlePress={onHandlePress}
                        >
                          {message.text}
                        </Hyperlink>
                      ) : null}
                      {renderExternal()}
                      {renderQuotedPost()}
                    </View>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          </View>
          {(message.images || message.video) && (
            <View style={{ width: '80%', alignSelf: 'flex-end', marginRight: 8 }}>
              {renderMedia()}
            </View>
          )}

          <MediaModal 
            visible={modalVisible} 
            media={selectedMedia} 
            onClose={() => setModalVisible(false)} 
          />

          <ActionMenu 
            visible={menuVisible}
            onClose={() => setMenuVisible(false)}
            onAction={executeAction}
            isLiked={isLiked}
            isReposted={isReposted}
            isMine={message.isMine}
            hashtags={hashtags}
          />
        </View>
      </Swipeable>
    );
  }

  return (
    <Swipeable
      ref={swipeableRef}
      renderRightActions={renderRightActions}
      onSwipeableOpen={handleSwipeOpen}
      friction={2}
      rightThreshold={40}
      enabled={true}
    >
      <View style={styles.otherMessageOuterContainer}>
        {renderRepostLabel()}
        <View style={styles.otherMessageContainer}>
          <TouchableOpacity 
            onPress={() => onSelectUser && message.authorDid && onSelectUser(message.authorDid)}
            activeOpacity={0.7}
          >
            <Image
              source={{ uri: message.avatarUrl || 'https://via.placeholder.com/40' }}
              style={styles.avatar}
              cachePolicy="memory-disk"
            />
          </TouchableOpacity>
          <View style={styles.otherContent}>
            <TouchableOpacity 
              onPress={() => onSelectUser && message.authorDid && onSelectUser(message.authorDid)}
              activeOpacity={0.7}
            >
              <Text style={styles.senderName}>{message.senderName}</Text>
            </TouchableOpacity>
            {message.text || message.quotedPost || message.replyParent ? (
              <View style={styles.otherBubbleRow}>
                <TouchableOpacity 
                  onPress={() => onSelectPost?.(message)}
                  onLongPress={handleLongPress} 
                  activeOpacity={0.8}
                >
                  <View style={styles.otherBubble}>
                    {renderReplyParent()}
                    {message.text ? (
                      <Hyperlink 
                        linkDefault={true} 
                        linkStyle={{ 
                          color: theme.colors.primary, 
                          textDecorationLine: 'underline' 
                        }} 
                        style={[
                          styles.messageText,
                          { fontSize: activeFontSizeScale.default },
                          Platform.OS === 'web' && {
                            maxWidth: Math.floor(windowWidth * 0.65),
                            width: '100%',
                            display: 'inline-block',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }
                        ]}
                        onHashtagPress={onSearchHashtag}
                        onHandlePress={onHandlePress}
                      >
                        {message.text}
                      </Hyperlink>
                    ) : null}
                    {renderExternal()}
                    {renderQuotedPost()}
                  </View>
                </TouchableOpacity>
                <Text style={styles.timeText}>{message.createdAt}</Text>
              </View>
            ) : null}
            {(message.images || message.video) && renderMedia()}
          </View>

          <MediaModal 
            visible={modalVisible} 
            media={selectedMedia} 
            onClose={() => setModalVisible(false)} 
          />

          <ActionMenu 
            visible={menuVisible}
            onClose={() => setMenuVisible(false)}
            onAction={executeAction}
            isLiked={isLiked}
            isReposted={isReposted}
            isMine={message.isMine}
            hashtags={hashtags}
          />
        </View>
      </View>
    </Swipeable>
  );
};

const ActionMenu: React.FC<{
  visible: boolean;
  onClose: () => void;
  onAction: (index: number, data?: any) => void;
  isLiked: boolean;
  isReposted: boolean;
  isMine: boolean;
  hashtags: string[];
}> = ({ visible, onClose, onAction, isLiked, isReposted, isMine, hashtags }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity 
        style={styles.menuOverlay} 
        activeOpacity={1} 
        onPress={onClose}
      >
        <View style={styles.menuContainer}>
          <TouchableOpacity style={styles.menuItem} onPress={() => onAction(0)}>
            <Ionicons name={isLiked ? "heart" : "heart-outline"} size={20} color={isLiked ? theme.colors.danger : theme.colors.text} />
            <Text style={[styles.menuText, isLiked && { color: theme.colors.danger }]}>
              {isLiked ? t('messageBubble.unlikeButton') : t('messageBubble.likeButton')}
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.menuItem} onPress={() => onAction(1)}>
            <MaterialCommunityIcons name="repeat" size={20} color={isReposted ? theme.colors.success : theme.colors.text} />
            <Text style={[styles.menuText, isReposted && { color: theme.colors.success }]}>
              {isReposted ? t('messageBubble.unrepostButton') : t('messageBubble.repostButton')}
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.menuItem} onPress={() => onAction(2)}>
            <MaterialCommunityIcons name="format-quote-close" size={20} color={theme.colors.text} />
            <Text style={styles.menuText}>{t('messageBubble.quote')}</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.menuItem} onPress={() => onAction(3)}>
            <Ionicons name="arrow-undo-outline" size={20} color={theme.colors.text} />
            <Text style={styles.menuText}>{t('messageBubble.reply')}</Text>
          </TouchableOpacity>

          {isMine && (
            <TouchableOpacity style={styles.menuItem} onPress={() => onAction(4)}>
              <Ionicons name="trash-outline" size={20} color={theme.colors.danger} />
              <Text style={[styles.menuText, { color: theme.colors.danger }]}>{t('common.delete')}</Text>
            </TouchableOpacity>
          )}

          {hashtags.length > 0 && (
            <>
              <View style={styles.menuSeparator} />
              {hashtags.map((tag, i) => (
                <TouchableOpacity key={i} style={styles.menuItem} onPress={() => onAction(5, tag)}>
                  <MaterialCommunityIcons name="pound" size={20} color={theme.colors.text} />
                  <Text style={styles.menuText}>{t('messageBubble.hashtagSearch', { tag })}</Text>
                </TouchableOpacity>
              ))}
            </>
          )}

          <TouchableOpacity style={styles.menuItem} onPress={() => onAction(6)}>
            <Ionicons name="bookmark-outline" size={20} color={theme.colors.text} />
            <Text style={styles.menuText}>{t('messageBubble.save')}</Text>
          </TouchableOpacity>

          <View style={styles.menuSeparator} />
          
          <TouchableOpacity style={styles.menuItem} onPress={onClose}>
            <Text style={[styles.menuText, { color: theme.colors.danger, textAlign: 'center', width: '100%', marginLeft: 0 }]}>
              {t('common.cancel')}
            </Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

const MediaModal: React.FC<{ 
  visible: boolean; 
  media: { url: string; type: 'image' | 'video' } | null; 
  onClose: () => void;
}> = ({ visible, media, onClose }) => {
  const styles = useThemedStyles(createStyles);
  if (!media) return null;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <SafeAreaView style={styles.modalContainer}>
        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <Ionicons name="close" size={30} color="white" />
        </TouchableOpacity>
        
        <View style={styles.modalContent}>
          {media.type === 'image' ? (
            <Image 
              source={{ uri: media.url }} 
              style={styles.fullImage} 
              contentFit="contain" 
              cachePolicy="memory-disk"
            />
          ) : (
            <Video
              source={{ uri: media.url }}
              style={styles.fullVideo}
              useNativeControls
              resizeMode={ResizeMode.CONTAIN}
              shouldPlay
            />
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  myMessageOuterContainer: {
    marginVertical: 4,
    paddingRight: 8,
    alignItems: 'flex-end',
    width: '100%',
  },
  otherMessageOuterContainer: {
    marginVertical: 4,
    paddingLeft: 8,
    alignItems: 'flex-start',
    width: '100%',
  },
  repostLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    backgroundColor: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  myRepostLabel: {
    marginRight: 12,
  },
  otherRepostLabel: {
    marginLeft: 48, // avatar width + margin
    alignSelf: 'flex-start',
  },
  repostLabelText: {
    fontSize: 10,
    color: theme.colors.textMuted,
    marginLeft: 4,
  },
  myMessageContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    width: '100%',
  },
  myMessageInfo: {
    alignItems: 'flex-end',
    marginRight: 4,
    marginBottom: 4,
    flexShrink: 0,
    minWidth: 0,
  },
  myBubbleRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    maxWidth: '100%',
    minWidth: 0,
  },
  readText: {
    fontSize: 10,
    color: theme.colors.textMuted,
  },
  timeText: {
    fontSize: 10,
    color: theme.colors.textMuted,
    flexShrink: 0,
    ...Platform.select({ 
      web: { 
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      } as any 
    }),
  },
  myContent: {
    alignItems: 'flex-end',
    maxWidth: '80%',
    flexShrink: 1,
    minWidth: 0,
  },
  myBubble: {
    backgroundColor: theme.colors.messageBubble,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    marginBottom: 4,
    flexShrink: 1,
    ...Platform.select({
      web: {
        maxWidth: '100%',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        overflow: 'hidden',
      } as any
    }),
  },
  otherMessageContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  avatar: {
    width: 33,
    height: 33,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: theme.colors.cardSecondary,
  },
  otherContent: {
    maxWidth: '80%',
    flexShrink: 1,
    minWidth: 0,
  },
  senderName: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 2,
  },
  otherBubbleRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    maxWidth: '100%',
    minWidth: 0,
  },
  otherMediaAndBubble: {
    alignItems: 'flex-start',
  },
  otherBubble: {
    backgroundColor: theme.isDark ? theme.colors.card : '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    marginRight: 4,
    marginBottom: 4,
    flexShrink: 1,
    borderWidth: theme.isDark ? 0.5 : 0,
    borderColor: theme.colors.border,
    ...Platform.select({
      web: {
        maxWidth: '100%',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        overflow: 'hidden',
      } as any
    }),
  },
  messageText: {
    fontSize: 15,
    color: theme.isDark ? '#FFFFFF' : '#000000',
    flexShrink: 1,
    minWidth: 0,
    ...Platform.select({ 
      web: { 
        wordBreak: 'break-word',
        overflowWrap: 'anywhere', 
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'clip',
      } as any 
    }),
  },
  myMessageText: {
    color: theme.colors.myMessageText,
  },
  replyInsideContainer: {
    marginBottom: 4,
    paddingBottom: 4,
  },
  replyInsideHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  replyInsideAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 6,
  },
  replyInsideInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  replyInsideAuthor: {
    fontSize: 11,
    fontWeight: 'bold',
    color: theme.colors.textMuted,
    marginRight: 4,
  },
  replyInsideText: {
    fontSize: 10,
    color: theme.colors.textMuted,
    flex: 1,
    ...Platform.select({ web: { whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' } as any }),
  },
  replySeparator: {
    height: 0.5,
    backgroundColor: theme.colors.border,
    marginTop: 4,
  },
  quoteContainer: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 8,
    padding: 8,
    marginTop: 4,
    borderWidth: 0.5,
    borderColor: theme.colors.border,
    ...(Platform.OS === 'web' ? { maxWidth: '100%', wordWrap: 'break-word' } as any : {}),
  },
  quoteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  quoteAvatar: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 4,
  },
  quoteAuthor: {
    fontSize: 11,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  quoteText: {
    fontSize: 12,
    color: theme.colors.text,
    ...Platform.select({ web: { whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' } as any }),
  },
  mediaContainer: {
    maxWidth: '100%',
  },
  videoThumbnailContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    maxHeight: 300,
    borderRadius: 12,
    marginBottom: 4,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  videoThumbnail: {
    width: '100%',
    height: '100%',
  },
  playIconOverlay: {
    position: 'absolute',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 40,
    padding: 10,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'black',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  modalContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImage: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
  },
  fullVideo: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT * 0.8,
  },
  swipeActionContainer: {
    width: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  swipeActionIcon: {
    backgroundColor: theme.colors.primary,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.6,
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContainer: {
    width: SCREEN_WIDTH * 0.7,
    backgroundColor: theme.colors.card,
    borderRadius: 14,
    paddingVertical: 8,
    overflow: 'hidden',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  menuText: {
    fontSize: 16,
    marginLeft: 12,
    color: theme.colors.text,
  },
  menuSeparator: {
    height: 0.5,
    backgroundColor: theme.colors.border,
    marginVertical: 4,
  },
  sensitiveContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    maxHeight: 200,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    marginBottom: 4,
  },
  sensitiveText: {
    color: theme.colors.text,
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 8,
    textAlign: 'center',
  },
  sensitiveSubtext: {
    color: theme.colors.textMuted,
    fontSize: 12,
    marginTop: 4,
  },
  externalContainer: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    marginTop: 8,
    overflow: 'hidden',
    borderWidth: 0.5,
    borderColor: theme.colors.border,
    width: '100%',
  },
  externalThumb: {
    width: '100%',
    height: 150,
  },
  externalContent: {
    padding: 10,
  },
  externalTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 4,
    ...Platform.select({ web: { wordWrap: 'break-word', overflowWrap: 'break-word' } as any }),
  },
  externalDescription: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 4,
    ...Platform.select({ web: { wordWrap: 'break-word', overflowWrap: 'break-word' } as any }),
  },
  externalUri: {
    fontSize: 11,
    color: theme.colors.textMuted,
    ...Platform.select({ web: { wordWrap: 'break-word', overflowWrap: 'break-word' } as any }),
  },
});

export default MessageBubble;

